/**
 * Access string parser class
 */

import { indexIn } from 'app/helpers/entity'

/**
 * AccessParserFactory
 *
 * @class AccessParserFactory
 * @memberof mwApp/auth
 * @see mwApp/auth
 * @return {Function} {@link models.Overlay} class
 *
 * @ngdoc factory
 * @name mwApp/auth.factory:AccessParserFactory
 */
/* @ngInject */
export default function accessParserFactory () {
  /* === private === */

  /**
   * Current user rules
   *
   * @memberof mwApp/auth.factory:AccessParserFactory~
   * @private
   * @type {models.Rule}
   */
  var rules = null

  /**
   * Cache for Access objects
   *
   * @memberof mwApp/auth.factory:AccessParserFactory~
   * @private
   * @type {Object}
   */
  var accessCache = Object.create(null)

  /* === StackElement class === */

  /**
   * Access operators array
   * @type {Array}
   */
  var accessOperators = ['IN', 'IF', 'ELSE', 'THEN', '&', '|', '!', '(+)', '=', 'SIZE']

  /**
   * Access operators indexes
   * They should be in accordance with accessOperators array
   */
  var O_IN = accessOperators.indexOf('IN')
  var O_IF = accessOperators.indexOf('IF')
  var O_ELSE = accessOperators.indexOf('ELSE')
  var O_THEN = accessOperators.indexOf('THEN')
  var O_AND = accessOperators.indexOf('&')
  var O_OR = accessOperators.indexOf('|')
  var O_NOT = accessOperators.indexOf('!')
  var O_UPLUS = accessOperators.indexOf('(+)')
  var O_EQ = accessOperators.indexOf('=')
  var O_SIZE = accessOperators.indexOf('SIZE')

  /**
   * Stack elements types
   */
  var E_PARAMETER = 0
  var E_DEEP_PARAMETER = 1
  var E_STRING = 2
  var E_OPERATOR = 3
  var E_RULE = 4

  /**
   * StackElement class
   *
   * @class StackElement
   * @classdesc StackElement class
   * @memberof mwApp/auth.factory:AccessParserFactory~
   * @private
   * @param {String} el - access string's single element
   * @return {StackElement}
   */
  function StackElement (el) {
    this.src = el

    // starts with ':' -> this is parameter
    if (el.indexOf(':') === 0) {
      if (el.indexOf('.') === -1) {
        this.type = E_PARAMETER
        this.value = el.slice(1)
      } else {
        this.type = E_DEEP_PARAMETER
        this.value = el.slice(1).split('.')
      }
      return
    }

    // bounded by " -> this is string
    if (el.match(/^["'].*["']$/)) {
      this.type = E_STRING
      this.value = el.replace(/^["']|["']$/g, '')
      return
    }

    // exists in accessOperators array -> this is operator
    var index = accessOperators.indexOf(el)
    if (index > -1) {
      this.type = E_OPERATOR
      this.value = index
      return
    }

    // otherwise -> this is rule flag
    this.type = E_RULE
    this.value = rules ? rules[el] : undefined
  }

  /* === Access class === */

  /**
   * Access class
   *
   * @class Access
   * @classdesc Access class
   * @memberof mwApp/auth.factory:AccessParserFactory~
   * @private
   * @param {String} access - access string
   * @return {Access}
   */
  function Access (access) {
    // split access string to elements by space (or just create new array with access string in it if there are no spaces)
    this.elements = access.indexOf(' ') > -1 ? access.split(/\s+/) : [access]

    var hasParams = false

    // prepare stack -> replace each element with StackElement
    this.stack = []
    for (var i = 0; i < this.elements.length; i++) {
      var el = new StackElement(this.elements[i])
      this.stack.push(el)
      if (!hasParams &&
          (el.type === E_PARAMETER ||
           el.type === E_DEEP_PARAMETER)
      ) {
        hasParams = true
      }
    }

    // run stack machine over empty params, to know what we will get in that case later
    this.empty = this._run({})

    // if there are no parameters -> replace check() method with _empty()
    if (!hasParams) {
      this.check = this._empty
    }
  }
  Access.prototype = {

    /**
     * Access string's elements
     * @type {String[]}
     */
    elements: null,

    /**
     * Access value for empty params object
     * @type {Boolean}
     */
    empty: null,

    /**
     * Prepared stack (with StackElements objects inplaces)
     * @type {StackElement[]}
     */
    stack: null,

    /**
     * Check access against given params
     *
     * @param  {Object} [params] - parameters for access object
     * @return {Boolean}
     */
    check: function (params) {
      return !params || isEmptyObject(params)
        ? this.empty
        : this._run(params)
    },

    /**
     * Return access value for empty params object
     *
     * @return {Boolean}
     */
    _empty: function () {
      return this.empty
    },

    /**
     * Run stack machine over prepared stack <br>
     * This is a very simple stack machine, like Forth, evaluate expression using Reverse Polish Notation, for example
     *   2 3 +  =>  2 + 3
     *   flag1 flag2 &  =>  flag1 & flag2
     * Supports IF condition
     * DUP, SWAP, ROT and similar simply operators could be easily added
     *
     * @param  {Object} params - parameters for access object
     * @return {Boolean}
     */
    _run: function (params) {
      var stack = []
      var skip = false // flag to indicate that we need to skip current statement
      var el, a, b

      // go through all prepared stack from start to end, and create new result stack
      for (var i = 0; i < this.stack.length; i++) {
        el = this.stack[i] // StackElement object

        // if in skip mode
        if (skip) {
          // this is not operator - continue skipping
          // this is operator, but not ELSE or THEN - continue skipping
          // this is operator, ELSE or THEN - finish skipping
          skip = el.type !== E_OPERATOR || (el.value !== O_ELSE && el.value !== O_THEN)
          continue
        }

        switch (el.type) {
          // String, gets given string and pushes it to stack
          //   "radio"
          case E_STRING:
            /* falls through */

          // Rule flags, gets given flag from user rules and pushes it to stack
          //   viewNetwork
          case E_RULE: // eslint-disable-line
            stack.push(el.value)
            break

          // Param, started with colon, gets params with given name and pushes it to stack
          //   :packageId
          case E_PARAMETER:
            stack.push(params[el.value])
            break

          // Param, started with colon and contains dots in name, gets params with given name and pushes it to stack
          //   :network.id
          case E_DEEP_PARAMETER:
            stack.push(deepFind(params, el.value))
            break

          // Operator
          case E_OPERATOR:
            switch (el.value) {
              // IN keyword, gets two top stack elements and checks if first in second
              //   <element> <array> IN
              case O_IN:
                a = stack.pop() // array
                b = stack.pop() // element
                stack.push(
                  a && b
                    ? (
                      b.id
                        ? a.length === 0 || indexIn(a)(b) !== -1 // ~ b instanceof Entity ? .. Entity.indexOf(a, b)
                        : (
                          Array.isArray(a)
                            ? a.indexOf(b) !== -1
                            : (b in a)
                        )
                    )
                    : false
                )
                break

              // IF condition, works like IF in Forth language:
              // gets top stack element, checks it and goes to commands after IF or after ELSE
              // ends with THEN keyword
              //   <condition> IF <smth> ELSE <smth> THEN
              case O_IF:
                skip = !stack.pop()
                break

              // ELSE keyword, runs commands if IF condition is falsy
              //   <condition> IF <smth> ELSE <smth> THEN
              case O_ELSE:
                skip = true
                break

              // THEN keyword finishes IF statement
              //   <condition> IF <smth> ELSE <smth> THEN
              case O_THEN:
                skip = false
                break

              // AND, gets two top stack elements, compares the with && and pushes result back to stack
              //   <smth> <smth> &
              case O_AND:
                a = stack.pop()
                b = stack.pop()
                stack.push(a && b)
                break

              // AND, gets two top stack elements, compares the with || and pushes result back to stack
              //   <smth> <smth> |
              case O_OR:
                a = stack.pop()
                b = stack.pop()
                stack.push(a || b)
                break

              // NOT, gets top stack element, invert it with ! and pushes result back to stack
              //   <smth> !
              case O_NOT:
                stack.push(!stack.pop())
                break

              // UNARY PLUS, gets top stack element, convert it to number using unary + operation and pushes result back to stack
              //   <smth> (+)
              case O_UPLUS:
                stack.push(+stack.pop())
                break

              // EQUAL, gets two top stack elements, compares the with === and pushes result back to stack
              //   <smth> <smth> =
              case O_EQ:
                a = stack.pop()
                b = stack.pop()
                stack.push(a === b)
                break

              // SIZE, gets array length (maybe object size too, in future)
              case O_SIZE:
                a = stack.pop()
                stack.push(Array.isArray(a) ? a.length : 0)
            }
        }
      }

      // return top element from stack, casted to boolean
      // or true, if stack is empty
      return stack.length ? !!stack.pop() : true
    }
  }

  /**
   * Helper function, just check if object is empty <br>
   * this is much faster, than Object.keys(obj).length === 0
   *
   * @memberof mwApp/auth.factory:AccessParserFactory~
   * @private
   * @param  {Object} obj - object to check
   * @return {Boolean}
   */
  function isEmptyObject (obj) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false
      }
    }
    return true
  }

  /**
   * Helper function to get deep object field value
   *
   * @memberof mwApp/auth.factory:AccessParserFactory~
   * @private
   * @param  {Object}   obj  - object
   * @param  {String[]} path - array with field path
   * @return {*} field value
   */
  function deepFind (obj, path) {
    for (var i = 0; i < path.length; i++) {
      var val = obj[path[i]]
      if (val === undefined || val === null) {
        return val
      }
      obj = val
    }
    return obj
  }

  /* === public === */

  /**
   * Public Access Parser interface
   *
   * @param  {String} [access] - access string
   * @return {Access} Access object
   */
  function accessParser (access) {
    if (!access) {
      access = 'authenticated'
    }

    if (!accessCache[access]) {
      accessCache[access] = new Access(access)
    }

    return accessCache[access]
  }

  /**
   * Set rules for accessParser
   *
   * @param {models.Rule} rule - current user rules
   */
  accessParser.setRules = function (rule) {
    rules = rule || { authenticated: false }

    // empty accesses cache, because there are new rules we have
    accessCache = Object.create(null)
  }

  // expose accessParser function to public
  return accessParser
}
